import { useCallback, useEffect, useState } from "react"
import { api } from "@/shared/api-client"
import { setApprovalContext } from "@/shared/approval"

interface Req {
  id: string
  title: string
  level: string
  state: string
  initiator: string
  created: number
  executable: boolean
}
interface Data {
  requests: Req[]
  prohibited: Record<string, { ts: number }>
  changes: { ts: number; text: string; ok: boolean }[]
}

const STATE: Record<string, string> = {
  pending: "ждёт решения", held: "отложено", running: "выполняется", done: "выполнено", stopped: "остановлено",
  denied: "отклонено", failed: "ошибка", expired: "истекло", rolled_back: "откатано", partly_rolled_back: "откатано частично",
  approved_unavailable: "разрешено, но полномочий нет",
}
const fmt = (t: number) => new Date(t * 1000).toLocaleString("ru-RU")

/** Владелец: запросы Джарвиса, «Зачем?», остановка/откат/запрет, журнал изменений. */
export function ControlCard() {
  const [d, setD] = useState<Data | null>(null)
  const [why, setWhy] = useState<Record<string, string>>({})
  const [err, setErr] = useState("")
  const load = useCallback(() => {
    api.get<Data>("/control/requests").then(setD).catch(() => setD(null))
  }, [])
  useEffect(() => {
    load()
    const t = setInterval(load, 10000)
    return () => clearInterval(t)
  }, [load])

  async function act(fn: () => Promise<unknown>) {
    setErr("")
    try {
      await fn()
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось")
    }
    load()
  }
  if (!d) return null
  const btn = "text-xs px-2.5 py-1 rounded-md border border-white/10 hover:bg-white/5"
  const open = d.requests.filter((r) => r.state === "pending" || r.state === "held")
  const running = d.requests.filter((r) => r.state === "running")
  const rest = d.requests.filter((r) => !["pending", "held", "running"].includes(r.state)).slice(0, 6)
  return (
    <div className="box overflow-hidden p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-zinc-200">Запросы Джарвиса</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          Решение относится только к одному запросу. Красные действия требуют код Google Authenticator. Можно спросить в
          чате: «зачем?», «останови», «отмени последнее изменение», «больше не делай этого без моего разрешения».
        </p>
      </div>
      {err && <p className="text-sm text-rose-400">{err}</p>}
      {open.length === 0 && running.length === 0 && <p className="text-sm text-zinc-500">Сейчас ничего не ждёт решения.</p>}
      {[...running, ...open].map((r) => (
        <div key={r.id} className="rounded-lg border border-white/10 p-3 space-y-2">
          <p className="text-sm text-zinc-200">
            {r.title} <span className="text-xs text-zinc-500">· {r.level === "red" ? "критическое" : "в рамках полномочий"} · {STATE[r.state]}</span>
          </p>
          <p className="text-xs text-zinc-500">{fmt(r.created)} · инициатор: {r.initiator}</p>
          {why[r.id] && <pre className="text-xs text-zinc-300 whitespace-pre-wrap">{why[r.id]}</pre>}
          <div className="flex flex-wrap gap-2">
            <button className={btn} onClick={() => act(async () => {
              const w = await api.get<{ explanation: string }>(`/control/requests/${r.id}/why`)
              setWhy((s) => ({ ...s, [r.id]: w.explanation }))
            })}>Зачем?</button>
            {r.state !== "running" && (
              <>
                <button className={btn} onClick={() => act(async () => {
                  setApprovalContext(`Разрешить: ${r.title}`)
                  await api.post(`/control/requests/${r.id}/decision`, { decision: "allow" })
                })}>Разрешить</button>
                <button className={btn} onClick={() => act(() => api.post(`/control/requests/${r.id}/decision`, { decision: "deny" }))}>Отклонить</button>
                <button className={btn} onClick={() => act(() => api.post(`/control/requests/${r.id}/decision`, { decision: "hold" }))}>Пока ничего</button>
              </>
            )}
            {r.state === "running" && <button className={btn} onClick={() => act(() => api.post("/control/stop", {}))}>Остановить</button>}
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <button className={btn} onClick={() => act(() => api.post("/control/undo", {}))}>Отменить последнее изменение</button>
      </div>
      {Object.keys(d.prohibited).length > 0 && (
        <p className="text-xs text-zinc-400">Запрещено без вашего разрешения: {Object.keys(d.prohibited).join(", ")}</p>
      )}
      {rest.length > 0 && (
        <div className="text-xs text-zinc-500">
          {rest.map((r) => (
            <div key={r.id}>{r.title} — {STATE[r.state] ?? r.state}</div>
          ))}
        </div>
      )}
      <details>
        <summary className="text-xs text-zinc-400 cursor-pointer">Журнал изменений</summary>
        {d.changes.map((c, i) => (
          <div key={i} className="text-xs text-zinc-500">{fmt(c.ts)} · {c.text}</div>
        ))}
      </details>
    </div>
  )
}
